import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  Plus,
  Trash2,
  Pencil,
  Shield,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import AppShell from "@/components/layout/AppShell";
import { authApi, programmesApi } from "@/lib/crmApi";
import { useAuth } from "@/context/AuthContext";

const blankForm = {
  email: "",
  password: "",
  name: "",
  role: "commercial",
  assigned_programmes: [],
};

/**
 * Admin-only page to manage staff users (commerciaux + co-admins). The CRM
 * keys all programme isolation off the `assigned_programmes` array — that's
 * what we expose as a checkbox grid against the available programmes.
 */
export default function UsersPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', user?, form}
  const [saving, setSaving] = useState(false);

  // Block non-admins early — defense in depth (the API also enforces this).
  useEffect(() => {
    if (!authLoading && me && me.role !== "admin") {
      toast.error("Réservé à l'administrateur");
      navigate("/", { replace: true });
    }
  }, [authLoading, me, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        authApi.listUsers(),
        programmesApi.list(),
      ]);
      setUsers(u);
      setProgrammes(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => setEditing({ mode: "create", form: { ...blankForm } });

  const openEdit = (u) =>
    setEditing({
      mode: "edit",
      user: u,
      form: {
        email: u.email,
        password: "",
        name: u.name || "",
        role: u.role || "commercial",
        assigned_programmes: u.assigned_programmes || [],
      },
    });

  const closeDialog = () => setEditing(null);

  const toggleProgramme = (nom) => {
    setEditing((e) => {
      if (!e) return e;
      const set = new Set(e.form.assigned_programmes);
      set.has(nom) ? set.delete(nom) : set.add(nom);
      return { ...e, form: { ...e.form, assigned_programmes: [...set] } };
    });
  };

  const save = async () => {
    if (!editing) return;
    const f = editing.form;
    if (!f.email || !f.email.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    if (editing.mode === "create" && (!f.password || f.password.length < 6)) {
      toast.error("Mot de passe : 6 caractères minimum");
      return;
    }
    if (f.role === "commercial" && f.assigned_programmes.length === 0) {
      toast.error("Assignez au moins un programme à ce commercial");
      return;
    }
    setSaving(true);
    try {
      if (editing.mode === "create") {
        await authApi.createUser({
          email: f.email.toLowerCase().trim(),
          password: f.password,
          name: f.name.trim(),
          role: f.role,
          assigned_programmes: f.assigned_programmes,
        });
        toast.success("Utilisateur créé");
      } else {
        const payload = {
          name: f.name.trim(),
          role: f.role,
          assigned_programmes: f.assigned_programmes,
        };
        // Optional password reset
        if (f.password && f.password.length >= 6) payload.password = f.password;
        await authApi.updateUser(editing.user.id, payload);
        toast.success("Utilisateur mis à jour");
      }
      closeDialog();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("Vous ne pouvez pas supprimer votre propre compte");
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${u.name || u.email} ?`)) return;
    try {
      await authApi.deleteUser(u.id);
      toast.success("Utilisateur supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const toggleActive = async (u) => {
    try {
      await authApi.updateUser(u.id, { is_active: !u.is_active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false }}>
      <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-label text-muted-foreground">Administration</p>
            <h1 className="font-heading flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
              <UsersIcon className="h-7 w-7 text-emerald-600" />
              Utilisateurs
            </h1>
            <p className="text-sm text-muted-foreground">
              {users.length} utilisateur{users.length > 1 ? "s" : ""} · Gérez les
              accès et programmes assignés
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="gap-2"
            data-testid="users-create-btn"
          >
            <Plus className="h-4 w-4" />
            Nouvel utilisateur
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-shimmer rounded-xl border border-border/60"
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="users-list">
            {users.map((u) => {
              const isMe = u.id === me?.id;
              return (
                <li
                  key={u.id}
                  className="flex items-start gap-3 rounded-xl border border-border/60 bg-card p-4"
                  data-testid={`user-row-${u.id}`}
                >
                  <div
                    className={`flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-bold ${
                      u.role === "admin"
                        ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    }`}
                  >
                    {(u.name || u.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-heading truncate text-sm font-bold">
                        {u.name || u.email}
                      </p>
                      {u.role === "admin" && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                          <Shield className="h-3 w-3" />
                          Admin
                        </span>
                      )}
                      {!u.is_active && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700 dark:bg-red-950/40 dark:text-red-300">
                          Désactivé
                        </span>
                      )}
                      {isMe && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                          (vous)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                    {u.role === "commercial" && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {(u.assigned_programmes || []).length === 0 ? (
                          <span className="text-[10px] italic text-amber-600">
                            Aucun programme assigné
                          </span>
                        ) : (
                          (u.assigned_programmes || []).map((nom) => (
                            <span
                              key={nom}
                              className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                            >
                              {nom}
                            </span>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-none items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => toggleActive(u)}
                      title={u.is_active ? "Désactiver" : "Réactiver"}
                      disabled={isMe}
                      data-testid={`user-toggle-${u.id}`}
                    >
                      {u.is_active ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(u)}
                      title="Modifier"
                      data-testid={`user-edit-${u.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                      onClick={() => remove(u)}
                      title="Supprimer"
                      disabled={isMe}
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === "create"
                ? "Nouvel utilisateur"
                : `Modifier ${editing?.user?.name || editing?.user?.email}`}
            </DialogTitle>
          </DialogHeader>

          {editing && (
            // Wrapped in a form with autoComplete="off" + dummy autofill-bait
            // inputs so Chrome stops pre-filling this dialog with the
            // current admin's saved credentials. Chrome ignores `off` on
            // single fields but respects it when the form is also flagged
            // AND when the password input uses `autoComplete="new-password"`.
            <form autoComplete="off" className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              {/* Hidden bait fields — Chrome targets the FIRST email+password
                  pair it finds for autofill. These two absorb that match so
                  the real visible fields stay clean. */}
              <input type="text" name="fakeusernameremembered" autoComplete="username" tabIndex={-1} style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />
              <input type="password" name="fakepasswordremembered" autoComplete="new-password" tabIndex={-1} style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nom complet</Label>
                  <Input
                    name="staff-fullname"
                    autoComplete="off"
                    value={editing.form.name}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        form: { ...editing.form, name: e.target.value },
                      })
                    }
                    placeholder="Nom complet"
                    data-testid="user-form-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    name="staff-email"
                    autoComplete="off"
                    value={editing.form.email}
                    disabled={editing.mode === "edit"}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        form: { ...editing.form, email: e.target.value },
                      })
                    }
                    placeholder="commercial@sogi.sn"
                    data-testid="user-form-email"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {editing.mode === "create"
                      ? "Mot de passe"
                      : "Nouveau mot de passe (optionnel)"}
                  </Label>
                  <Input
                    type="password"
                    name="staff-new-password"
                    autoComplete="new-password"
                    value={editing.form.password}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        form: { ...editing.form, password: e.target.value },
                      })
                    }
                    placeholder="6 caractères minimum"
                    data-testid="user-form-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Rôle</Label>
                  <Select
                    value={editing.form.role}
                    onValueChange={(v) =>
                      setEditing({
                        ...editing,
                        form: { ...editing.form, role: v },
                      })
                    }
                  >
                    <SelectTrigger data-testid="user-form-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">
                  Programmes accessibles{" "}
                  {editing.form.role === "admin" && (
                    <span className="ml-1 text-[10px] italic text-muted-foreground">
                      (les admins voient tous les programmes — sélection
                      ignorée)
                    </span>
                  )}
                </Label>
                <div className="grid gap-1.5 rounded-lg border border-border/60 bg-muted/20 p-2 sm:grid-cols-2">
                  {programmes.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">
                      Aucun programme défini. Créez-en dans Programmes d'abord.
                    </p>
                  ) : (
                    programmes.map((p) => {
                      const checked = editing.form.assigned_programmes.includes(
                        p.nom
                      );
                      return (
                        <label
                          key={p.id}
                          className="flex cursor-pointer items-center gap-2 rounded p-1.5 text-sm hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleProgramme(p.nom)}
                            disabled={editing.form.role === "admin"}
                            data-testid={`user-form-prog-${p.id}`}
                          />
                          <span className="truncate">{p.nom}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </form>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={saving}>
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="user-form-save"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
